import type { FeatureContribution } from './feature-contribution';
import { BALANCES_FEATURES } from './balances.features';
import { BULK_EDIT_FEATURES } from './bulk-edit.features';
import { PUBLIC_API_FEATURES } from './public-api.features';
import { TIMETRACKING_FEATURES } from '@zag/timetracking/common';
import { PLANNING_FEATURES } from '@zag/planning/common';
import { PROJECTS_FEATURES } from '@zag/projects/common';
import { NOTIFICATIONS_FEATURES } from '@zag/notifications/common';
import { DOCUMENTS_FEATURES } from '@zag/documents/models';
import { OUTLOOK_FEATURES } from './outlook.features';
import { AI_FEATURES } from '@zag/ai/models';
import { WORKFLOWS_FEATURES } from '@zag/workflows/models';
import { SEARCH_FEATURES } from './search.features';
import { CALENDAR_FEATURES } from '@zag/calendar/models';
import { LOCATIONS_FEATURES } from '@zag/locations/models';
import { STORAGE_FEATURES } from '@zag/storage/common';
import { EMPLOYEES_FEATURES } from '@zag/employees/common';
import { EMAIL_FEATURES } from '@zag/email/common';
import { REPORTS_FEATURES } from '@zag/reports/common';
import { TERMINALS_FEATURES } from '@zag/terminals/models';

const CONTRIBUTIONS = [
  BALANCES_FEATURES,
  TIMETRACKING_FEATURES,
  PLANNING_FEATURES,
  PROJECTS_FEATURES,
  NOTIFICATIONS_FEATURES,
  PUBLIC_API_FEATURES,
  DOCUMENTS_FEATURES,
  OUTLOOK_FEATURES,
  AI_FEATURES,
  WORKFLOWS_FEATURES,
  SEARCH_FEATURES,
  CALENDAR_FEATURES,
  LOCATIONS_FEATURES,
  STORAGE_FEATURES,
  EMPLOYEES_FEATURES,
  EMAIL_FEATURES,
  REPORTS_FEATURES,
  TERMINALS_FEATURES,
  BULK_EDIT_FEATURES,
] as const satisfies readonly FeatureContribution[];

export type ModuleKey = (typeof CONTRIBUTIONS)[number]['moduleKey'];
export const MODULE_CATALOG: readonly ModuleKey[] = CONTRIBUTIONS.map(c => c.moduleKey) as unknown as readonly ModuleKey[];

/**
 * All module contributions, exposed for consumers that need to iterate
 * features grouped by their owning module (e.g. a feature toggle grid UI).
 */
export const FEATURE_MODULES: readonly FeatureContribution[] = CONTRIBUTIONS;

/**
 * Reverse lookup: fine-grained feature key -> its owning module key.
 * Powers runtime derivation of module-level access when a licence only
 * grants sub-features (e.g. `timetracking.balances` -> `timetracking`).
 * Without this, activating a licence whose features live under a module
 * would never flip `hasFeature('<moduleKey>')` to true.
 */
export const FEATURE_TO_MODULE: ReadonlyMap<string, ModuleKey> = new Map(
  CONTRIBUTIONS.flatMap(c => c.features.map((f: string) => [f as string, c.moduleKey] as const))
);

export const FEATURE_CATALOG = [
  ...BALANCES_FEATURES.features,
  ...TIMETRACKING_FEATURES.features,
  ...PLANNING_FEATURES.features,
  ...PROJECTS_FEATURES.features,
  ...NOTIFICATIONS_FEATURES.features,
  ...PUBLIC_API_FEATURES.features,
  ...DOCUMENTS_FEATURES.features,
  ...OUTLOOK_FEATURES.features,
  ...AI_FEATURES.features,
  ...WORKFLOWS_FEATURES.features,
  ...SEARCH_FEATURES.features,
  ...CALENDAR_FEATURES.features,
  ...LOCATIONS_FEATURES.features,
  ...STORAGE_FEATURES.features,
  ...EMPLOYEES_FEATURES.features,
  ...EMAIL_FEATURES.features,
  ...REPORTS_FEATURES.features,
  ...TERMINALS_FEATURES.features,
  ...BULK_EDIT_FEATURES.features,
] as const;

export const SPEC_FIXTURE_FEATURE_KEYS = ['test.gated-endpoint', 'test.flag-only-endpoint'] as const;

export type FeatureKey = (typeof FEATURE_CATALOG | typeof SPEC_FIXTURE_FEATURE_KEYS)[number];

export function isFeatureKey(value: string): value is FeatureKey {
  return [...FEATURE_CATALOG, ...SPEC_FIXTURE_FEATURE_KEYS].includes(value as FeatureKey);
}
